/**
 * W0 — Business Object Contract
 * UniversalBaseEntity workbench 승격 — additive 필드 + 접근자.
 */

import type { UniversalBaseEntity } from '@shared/types/entity';
import type { ObjectResourceSummary } from './resource-types';
import {
  DEFAULT_WORKBENCH_DENSITY,
  resolveWorkbenchDensity,
  type WorkbenchDensity,
} from './density-governance';

export type WorkbenchDensityMode = WorkbenchDensity;

export interface RelatedFlowRef {
  flowId: string;
  status?: string;
  flowTemplateId?: string;
  label?: string;
}

export interface RelatedRecordRef {
  id: string;
  businessDomain?: string;
  label?: string;
}

export interface BusinessObjectWorkbenchFields {
  displayName?: string;
  primaryActionRef?: string;
  /** @deprecated Read fallback only — SSOT: object_resource_summaries (RL0 §1.1) */
  relatedFlows?: RelatedFlowRef[];
  relatedRecords?: RelatedRecordRef[];
  /** @deprecated Read fallback only — use activity refs + lastActivityAt (RL0 §1.1) */
  timelineSummary?: string;
  /** @deprecated Read fallback only — SSOT: resourceSummary.publicVisibility (RL0 §1.1) */
  publicVisibility?: 'hidden' | 'draft' | 'published';
  permissionHints?: string[];
  density?: WorkbenchDensityMode;
  /** Runtime merge from ObjectResourceProvider — not persisted on entity JSON (D6) */
  resourceSummary?: ObjectResourceSummary;
}

export type BusinessObjectContract = UniversalBaseEntity & BusinessObjectWorkbenchFields;

export function getDisplayName(entity: UniversalBaseEntity): string {
  const displayName = (entity as BusinessObjectContract).displayName;
  if (typeof displayName === 'string' && displayName.trim().length > 0) {
    return displayName;
  }
  return entity.name;
}

export function getPrimaryActionRef(entity: UniversalBaseEntity): string | undefined {
  const ref = (entity as BusinessObjectContract).primaryActionRef;
  return typeof ref === 'string' ? ref : undefined;
}

export function getRelatedFlows(entity: UniversalBaseEntity): RelatedFlowRef[] {
  const flows = (entity as BusinessObjectContract).relatedFlows;
  return Array.isArray(flows) ? flows : [];
}

export function getRelatedRecords(entity: UniversalBaseEntity): RelatedRecordRef[] {
  const records = (entity as BusinessObjectContract).relatedRecords;
  return Array.isArray(records) ? records : [];
}

export function getTimelineSummary(entity: UniversalBaseEntity): string | undefined {
  const summary = (entity as BusinessObjectContract).timelineSummary;
  return typeof summary === 'string' ? summary : undefined;
}

export function getPublicVisibility(
  entity: UniversalBaseEntity
): BusinessObjectWorkbenchFields['publicVisibility'] {
  const value = (entity as BusinessObjectContract).publicVisibility;
  if (value === 'hidden' || value === 'draft' || value === 'published') {
    return value;
  }
  return undefined;
}

export function getPermissionHints(entity: UniversalBaseEntity): string[] {
  const hints = (entity as BusinessObjectContract).permissionHints;
  return Array.isArray(hints) ? hints : [];
}

export function isPubliclyVisible(entity: UniversalBaseEntity): boolean {
  return getPublicVisibility(entity) === 'published';
}

export function hasOpenFlows(entity: UniversalBaseEntity): boolean {
  return getRelatedFlows(entity).some(
    (flow) => flow.status !== 'completed' && flow.status !== 'cancelled'
  );
}

/** D-1 — resolves density with standard default when unset */
export function getWorkbenchDensity(entity: UniversalBaseEntity): WorkbenchDensityMode {
  const density = (entity as BusinessObjectContract).density;
  return resolveWorkbenchDensity(density !== undefined ? { density } : undefined);
}

export { DEFAULT_WORKBENCH_DENSITY };
